import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { ScrollText, Filter, X } from 'lucide-react';

const ROLE_COLOR = { office: '#1D4ED8', rector: '#C89B3C', system: '#64748B' };
const ROLE_LABEL = { office: 'Office', rector: 'Rector / Management', system: 'System' };

const EMPTY = { date_from: '', date_to: '', actor: '', role: '', applicant: '', case_id: '', action: '' };

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);

  const load = (params) => {
    setLoading(true);
    const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v));
    api.get('/audit', { params: clean })
      .then((r) => setLogs(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(EMPTY); }, []);

  const actionTypes = useMemo(() => [...new Set(logs.map((l) => l.action))].sort(), [logs]);
  const users = useMemo(() => [...new Set(logs.map((l) => l.user).filter(Boolean))].sort(), [logs]);

  const apply = () => { setApplied(filters); load(filters); };
  const reset = () => { setFilters(EMPTY); setApplied(EMPTY); load(EMPTY); };
  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));
  const activeCount = Object.values(applied).filter(Boolean).length;

  const Field = ({ label, children }) => (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-[#64748B]">{label}</span>
      {children}
    </label>
  );
  const inputCls = 'w-full rounded-lg border border-[#E2E8F0] bg-white px-2.5 py-1.5 text-sm outline-none focus:border-action';

  return (
    <div className="space-y-5" data-testid="audit-page">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-navy text-gold"><ScrollText size={20} /></span>
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#172033]">System Audit Log</h1>
          <p className="text-sm text-[#64748B]">Immutable, security &amp; accountability record — Rector / Management only.</p>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-[#E2E8F0] bg-white p-4" data-testid="audit-filters">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#172033]">
          <Filter size={15} /> Filters {activeCount > 0 && <span className="rounded-full bg-action/10 px-2 py-0.5 text-xs text-action">{activeCount} active</span>}
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
          <Field label="From"><input data-testid="audit-filter-from" type="date" value={filters.date_from} onChange={set('date_from')} className={inputCls} /></Field>
          <Field label="To"><input data-testid="audit-filter-to" type="date" value={filters.date_to} onChange={set('date_to')} className={inputCls} /></Field>
          <Field label="User">
            <select data-testid="audit-filter-user" value={filters.actor} onChange={set('actor')} className={inputCls}>
              <option value="">All users</option>
              {users.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </Field>
          <Field label="Role">
            <select data-testid="audit-filter-role" value={filters.role} onChange={set('role')} className={inputCls}>
              <option value="">All roles</option>
              <option value="office">Office</option>
              <option value="rector">Rector / Management</option>
              <option value="system">System</option>
            </select>
          </Field>
          <Field label="Action">
            <select data-testid="audit-filter-action" value={filters.action} onChange={set('action')} className={inputCls}>
              <option value="">All actions</option>
              {actionTypes.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </Field>
          <Field label="Applicant"><input data-testid="audit-filter-applicant" value={filters.applicant} onChange={set('applicant')} placeholder="Name…" className={inputCls} /></Field>
          <Field label="Case ID"><input data-testid="audit-filter-case" value={filters.case_id} onChange={set('case_id')} placeholder="case_id" className={inputCls} /></Field>
        </div>
        <div className="mt-3 flex gap-2">
          <button data-testid="audit-apply-filters" onClick={apply} className="rounded-lg bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy-dark">Apply</button>
          <button data-testid="audit-reset-filters" onClick={reset} className="flex items-center gap-1 rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm font-medium text-[#475569] hover:bg-[#F1F5F9]"><X size={14} /> Reset</button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-[#E2E8F0] bg-white">
        <table className="w-full min-w-[900px] text-sm">
          <thead>
            <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC] text-left">
              {['Timestamp', 'User', 'Role', 'Action', 'Admission Case', 'Previous Value', 'New Value', 'Description'].map((h) => (
                <th key={h} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-navy">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {logs.map((l) => (
              <tr key={l.id} data-testid="audit-row" className="border-b border-[#F1F5F9] align-top last:border-0">
                <td className="whitespace-nowrap px-4 py-2.5 text-xs text-[#64748B]">{new Date(l.timestamp).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                <td className="px-4 py-2.5 text-[#475569]">{l.user}</td>
                <td className="px-4 py-2.5"><span className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase" style={{ color: ROLE_COLOR[l.role] || '#64748B', backgroundColor: `${ROLE_COLOR[l.role] || '#64748B'}14` }}>{ROLE_LABEL[l.role] || l.role}</span></td>
                <td className="px-4 py-2.5 font-semibold text-[#172033]">{l.action}</td>
                <td className="px-4 py-2.5 text-[#475569]">{l.applicant_name ? <span>{l.applicant_name}<span className="block text-[11px] text-[#94A3B8]">{l.case_number}</span></span> : '—'}</td>
                <td className="px-4 py-2.5 text-[#94A3B8]">{l.prev_value ?? '—'}</td>
                <td className="px-4 py-2.5 text-[#475569]">{l.new_value ?? '—'}</td>
                <td className="px-4 py-2.5 text-[#475569]">{l.detail || '—'}</td>
              </tr>
            ))}
            {!loading && logs.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-[#94A3B8]">No audit entries match these filters.</td></tr>}
            {loading && <tr><td colSpan={8} className="px-4 py-10 text-center text-[#94A3B8]">Loading…</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
